import { clsx } from 'clsx';
import {
  MARK_LINK,
  MARK_TEXT_STYLE,
  MARK_UNDERLINE,
  NODE_HEADING,
  NODE_HR,
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_UL,
  RenderOptions,
} from 'storyblok-rich-text-react-renderer';

import { Fragment } from 'react';

import { Typography, SharedLink } from '@monoimin/atoms';

import { checkParagraphContainsOnlyFlag } from '@monoimin/utils/helpers';
import { ValidBlok } from '@monoimin/ui-core';

import { newRenderWithMarks } from '@monoimin/atoms/utils';

import { SbRichTextProps } from '../types';

import { levelToVariant } from './levelToVariant';

export const getAllResolvers = ({
  theme,
  blokResolvers,
  classNames,
}: SbRichTextProps) => {
  const listCombinedClassName = clsx(
    classNames?.list,
    classNames?.paragraph || 'body'
  );

  const resolvers: RenderOptions = {
    nodeResolvers: {
      [NODE_PARAGRAPH]: (children) => {
        return (
          <Typography className={classNames?.paragraph} theme={theme}>
            {newRenderWithMarks({ children })}
          </Typography>
        );
      },
      [NODE_HR]: () => {
        return (
          <hr className='border-transparent border-b h-[1px] border-b-line my-9!' />
        );
      },
      [NODE_HEADING]: (children, props) => {
        // для среднего размера флага в сториблок ричтексте добавляем regex флага в h3, проверка нужна чтобы вместо постой h3 рендерит p
        const containsOnlyFlag = checkParagraphContainsOnlyFlag(
          (children as string[])?.[0]
        );
        if (containsOnlyFlag) {
          return (
            <Typography
              flagSize={props.level === 3 ? 'medium' : 'small'}
              className={clsx(levelToVariant(props.level), 'mb-3.5 ')}
            >
              {children}
            </Typography>
          );
        }
        return (
          <Typography
            as={levelToVariant(props.level)}
            flagSize={props.level === 3 ? 'medium' : 'small'}
            className={clsx(levelToVariant(props.level), 'mb-3.5 ')}
          >
            {children}
          </Typography>
        );
      },
      [NODE_OL]: (children) => (
        <ol className={clsx('ordered-list', listCombinedClassName)}>
          {children}
        </ol>
      ),
      [NODE_UL]: (children) => (
        <ul className={clsx('unordered-list', listCombinedClassName)}>
          {children}
        </ul>
      ),

      [NODE_LI]: (children) => (
        <li className={clsx('pl-1', classNames?.li)}>
          {newRenderWithMarks({ children })}
        </li>
      ),
    },

    markResolvers: {
      [MARK_LINK]: (children, props) => {
        if (!props.href) {
          return (
            <Typography className={clsx(classNames?.paragraph)} theme={theme}>
              {children}
            </Typography>
          );
        }
        const additionalparams =
          typeof children === 'string' ? { 'aria-label': children } : {};
        return (
          <SharedLink
            theme={theme}
            linkType={props?.linktype === 'story' ? 'internal' : 'external'}
            {...additionalparams}
            href={props.href}
            underline='hover'
            className={clsx(
              classNames?.paragraph,
              classNames?.link,
              'shared-link-rich-text ghost-link'
            )}
          >
            {newRenderWithMarks({ children })}
          </SharedLink>
        );
      },
      [MARK_TEXT_STYLE]: (children) => {
        return <span>{children}</span>;
      },
      // по дефолту ричтекст рендерит вложенный тег U с котором долгое время боролись, искали решение в других местах, но нужно было просто переписать резолвер
      [MARK_UNDERLINE]: (children) => {
        return <Fragment>{children}</Fragment>;
      },
    },
    blokResolvers,
    defaultBlokResolver: (name) => (
      <ValidBlok blok={undefined} blokName={name} />
    ),
  };
  return resolvers;
};
